import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  NotificationsActive as LaunchIcon,
  StopCircle as StopIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAdminCircle,
  getAdminCircles,
  getAdminPairs,
  stopAdminCircle,
} from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { useAdminTime } from '@/contexts/AdminTimeContext';
import type { AdminCircle, ArbitragePair } from '@/types';

const DEFAULT_SIGNAL_SPREAD_PERCENT = 0.5;
const DEFAULT_NEGATIVE_SPREAD_PERCENT = -0.2;
const DEFAULT_COOLDOWN_HOURS = 12;

type CircleFormData = {
  pair_id: number | '';
  signal_spread_percent: number;
  negative_spread_percent: number;
  cooldown_hours: number;
  expires_in_hours: number | '';
  notes: string;
};

const numberInputProps = {
  inputMode: 'decimal' as const,
};

function pairLabel(pair?: Pick<ArbitragePair, 'pair_type' | 'coin_from' | 'coin_to' | 'exchange_buy_name' | 'exchange_sell_name' | 'exchange_buy' | 'exchange_sell'> | null) {
  if (!pair) return '';
  const coins = pair.pair_type === 'double' ? `${pair.coin_from} -> ${pair.coin_to}` : pair.coin_from;
  return `${coins} | ${pair.exchange_buy_name || pair.exchange_buy} -> ${pair.exchange_sell_name || pair.exchange_sell}`;
}

export default function AdminCircles() {
  const queryClient = useQueryClient();
  const { t } = useAdminI18n();
  const { formatDateTime } = useAdminTime();
  const [formData, setFormData] = useState<CircleFormData>({
    pair_id: '',
    signal_spread_percent: DEFAULT_SIGNAL_SPREAD_PERCENT,
    negative_spread_percent: DEFAULT_NEGATIVE_SPREAD_PERCENT,
    cooldown_hours: DEFAULT_COOLDOWN_HOURS,
    expires_in_hours: '',
    notes: '',
  });

  const { data: pairsData, isLoading: pairsLoading } = useQuery({
    queryKey: ['admin-pairs'],
    queryFn: getAdminPairs,
  });

  const { data: circlesData, isLoading: circlesLoading, error } = useQuery({
    queryKey: ['admin-circles'],
    queryFn: () => getAdminCircles(),
  });

  const pairs = pairsData?.pairs ?? [];
  const circles = circlesData?.circles ?? [];
  const selectedPair = useMemo(
    () => pairs.find((pair) => pair.id === formData.pair_id),
    [formData.pair_id, pairs],
  );
  const activeCircleCount = circles.filter((circle) => circle.status === 'active').length;

  useEffect(() => {
    if (!selectedPair) return;
    setFormData((current) => ({
      ...current,
      signal_spread_percent: current.signal_spread_percent || selectedPair.base_spread_percent || DEFAULT_SIGNAL_SPREAD_PERCENT,
      negative_spread_percent: selectedPair.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT,
    }));
  }, [selectedPair]);

  const invalidateSignalQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-circles'] });
    queryClient.invalidateQueries({ queryKey: ['pairs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-pairs'] });
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const expiresAt = formData.expires_in_hours
        ? new Date(Date.now() + formData.expires_in_hours * 60 * 60 * 1000).toISOString()
        : null;

      return createAdminCircle({
        pair_id: Number(formData.pair_id),
        signal_spread_percent: formData.signal_spread_percent,
        negative_spread_percent: formData.negative_spread_percent,
        cooldown_hours: formData.cooldown_hours,
        expires_at: expiresAt,
        notes: formData.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      invalidateSignalQueries();
      setFormData((current) => ({ ...current, notes: '' }));
    },
  });

  const stopMutation = useMutation({
    mutationFn: stopAdminCircle,
    onSuccess: invalidateSignalQueries,
  });

  if (pairsLoading || circlesLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <SectionHeader
        title={t('admin.circles.title')}
        detail={t('admin.circles.detail', { active: activeCircleCount, total: circles.length })}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.circles.loadError')}</Alert>}
      {createMutation.error && <Alert severity="error" sx={{ mb: 2 }}>{t('admin.circles.launchError')}</Alert>}

      <Box
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          border: '1px solid rgba(226, 255, 238, 0.12)',
          bgcolor: 'rgba(244, 255, 249, 0.045)',
        }}
      >
        <Stack spacing={1.5}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('admin.circles.pair')}</InputLabel>
            <Select
              value={formData.pair_id}
              label={t('admin.circles.pair')}
              onChange={(event) => setFormData({ ...formData, pair_id: Number(event.target.value) })}
            >
              {pairs.map((pair) => (
                <MenuItem key={pair.id} value={pair.id}>
                  {pairLabel(pair)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1.25 }}>
            <TextField
              size="small"
              label={t('admin.circles.signalSpread')}
              type="number"
              inputProps={numberInputProps}
              value={formData.signal_spread_percent}
              onChange={(event) => setFormData({ ...formData, signal_spread_percent: parseFloat(event.target.value) || 0 })}
            />
            <TextField
              size="small"
              label={t('admin.circles.negativeSpread')}
              type="number"
              inputProps={numberInputProps}
              value={formData.negative_spread_percent}
              onChange={(event) => setFormData({ ...formData, negative_spread_percent: parseFloat(event.target.value) || 0 })}
            />
            <TextField
              size="small"
              label={t('admin.circles.cooldownHours')}
              type="number"
              inputProps={numberInputProps}
              value={formData.cooldown_hours}
              onChange={(event) => setFormData({ ...formData, cooldown_hours: parseInt(event.target.value, 10) || DEFAULT_COOLDOWN_HOURS })}
            />
            <TextField
              size="small"
              label={t('admin.circles.expiresIn')}
              type="number"
              inputProps={numberInputProps}
              value={formData.expires_in_hours}
              onChange={(event) => {
                const value = event.target.value ? parseInt(event.target.value, 10) : '';
                setFormData({ ...formData, expires_in_hours: value || '' });
              }}
            />
          </Box>

          <TextField
            size="small"
            label={t('admin.circles.notes')}
            value={formData.notes}
            onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
            multiline
            rows={2}
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {selectedPair ? pairLabel(selectedPair) : t('admin.circles.noPairSelected')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              onClick={() => createMutation.mutate()}
              disabled={!formData.pair_id || createMutation.isPending}
            >
              {t('admin.circles.launch')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.common.status')}</TableCell>
              <TableCell>{t('admin.circles.pair')}</TableCell>
              <TableCell>{t('admin.circles.signalSpreadShort')}</TableCell>
              <TableCell>{t('admin.circles.cooldownShort')}</TableCell>
              <TableCell>{t('admin.circles.notifications')}</TableCell>
              <TableCell>{t('admin.circles.started')}</TableCell>
              <TableCell>{t('admin.common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {circles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.circles.empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              circles.map((circle: AdminCircle) => (
                <TableRow key={circle.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={circle.status === 'active' ? t('admin.common.active') : t('admin.common.inactive')}
                      color={circle.status === 'active' ? 'success' : 'default'}
                      variant={circle.status === 'active' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>{pairLabel(circle as AdminCircle & ArbitragePair)}</TableCell>
                  <TableCell>{circle.signal_spread_percent}%</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2">{circle.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT}%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {circle.cooldown_hours}h
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {(circle.sent_notifications ?? 0) + (circle.failed_notifications ?? 0)} / {circle.transaction_count ?? 0}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2">{formatDateTime(circle.started_at)}</Typography>
                      {circle.expires_at && (
                        <Typography variant="caption" color="text.secondary">
                          {t('admin.circles.expires')}: {formatDateTime(circle.expires_at)}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      disabled={circle.status !== 'active' || stopMutation.isPending}
                      onClick={() => stopMutation.mutate(circle.id)}
                    >
                      {t('admin.circles.stop')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}